import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { AlgoStrategyLookupService } from "shared/cubiq-services/lookup/model/impl/AlgoStrategyLookupService";
import { BmarkTraderGroupNameHelper } from "shared/cubiq-services/model/benchmarks/helpers/BmarkTradeGroupNameHelper";
import { OrderTypeHelper } from "shared/cubiq-services/model/orders/OrderTypeHelper";
import { BfxSysConfigUtilService } from "shared/auth-services";
import { TraderGroupLookupService } from "../../lookup/model/impl/TraderGroupLookupService";
import { TraderGroupPromiseLookupService } from "../../lookup/model/impl/TraderGroupPromiseLookupService";
import { BMRiskUtils } from "shared/cubiq-services/model/benchmarks/helpers/BMRiskUtils";

/**
 * Provide Order Description text used to describe an order on various dialogs in the OMS e.g.
 * Order Cancel, take, steal., force-take etc
 */
export class OrderDescriptionHelper {
  private _traderGroupNameHelper: BmarkTraderGroupNameHelper;

  private traderGroupLookupService: TraderGroupLookupService;
  private orderTypeHelper: OrderTypeHelper;

  constructor(
    private tradeGroupService: TraderGroupPromiseLookupService,
    private algoStrategyLookupService: AlgoStrategyLookupService,
    private sysConfigUtil: BfxSysConfigUtilService,
  ) {
    this.initialize();
  }

  protected initialize(): void {
    this.orderTypeHelper = new OrderTypeHelper(
      "",
      this.algoStrategyLookupService,
      this.sysConfigUtil,
    );
    this.traderGroupLookupService = this.tradeGroupService.getBase();
    this._traderGroupNameHelper = new BmarkTraderGroupNameHelper(this.traderGroupLookupService, "");
  }

  /**
   * Creates order info from a raw adapter order data
   * @param order the raw order data record
   */
  public createOrderDescription(order: CubiqTS.OrderDataImpl): string {
    let requestedOrderType: CubiqTS.RequestOrderType = order.requestedOrderType;

    let isAlgoOrder: boolean =
      order.orderType == CubiqTS.OrderType.ALGO || order.orderType == CubiqTS.OrderType.BMALGO;
    let isSlOrder: boolean = this.isSL(requestedOrderType);
    let isTpOrder: boolean = this.isTP(requestedOrderType);
    let isBmOrder: boolean = this.isBmark(requestedOrderType);
    let isMarketOrder: boolean = this.isMarket(requestedOrderType);
    let isCallOrder: boolean = order.requestedOrderType === CubiqTS.RequestOrderType.CALL;
    let isStrategy: boolean = this.isStrategy(order);

    let clientName: string = order.clientName;
    let clientIntent: string = order.clientIntent == CubiqTS.OrderIntent.BUY ? "Buys" : "Sells";
    let ccyPair: string = order.isInvertedOrderFg
      ? order.nonBaseCode + "/" + order.baseCode
      : order.baseCode + "/" + order.nonBaseCode;
    let dealtCCY: string = order.fixedCode;
    let dealtAmt: string = order.leavesQty ? order.leavesQty.toFormat(0) : "0";
    let requestRt: string = order.requestRt ? order.requestRt.toString() : "-";
    let orderTypeCol: string = this.orderTypeHelper.getType(order as any);

    const strategyDesc = isStrategy ? `(Strategy ${order.strategyRef})` : "";
    let orderInfo: string;

    let isComponentLeg: boolean = false;
    let riskID: string = "";
    if (isBmOrder) {
      let allocation: CubiqTS.AllocationDataImpl = order.allocationData[0];
      if (allocation) {
        if (order.allocationData?.length > 1) {
          // For multi-allocation BMs we need to recalc the amounts from the allocations
          const riskAggregate = BMRiskUtils.aggregateAllocations(
            order.fixedCode,
            order.product,
            order.allocationData,
          );
          clientIntent = riskAggregate.clientIntent == CubiqTS.OrderIntent.BUY ? "Buys" : "Sells";
          dealtAmt = riskAggregate.riskAmount.toFormat(0);
        } else {
          clientIntent = allocation.allocationIntent == CubiqTS.OrderIntent.BUY ? "Buys" : "Sells";
          dealtAmt = allocation.allocationQty.toFormat(0);
        }

        if (
          (allocation.allocationType === CubiqTS.AllocationType.PRIME ||
            allocation.allocationType === CubiqTS.AllocationType.UNSPAWNEDCOMPONENT) &&
          order.isInvertedOrderFg
        ) {
          ccyPair = allocation.nonbaseCcy + "/" + allocation.baseCcy;
        } else {
          ccyPair = allocation.baseCcy + "/" + allocation.nonbaseCcy;
        }

        dealtCCY = allocation.fixedCcy;

        isComponentLeg =
          allocation.allocationType === CubiqTS.AllocationType.COMPONENT ||
          allocation.allocationType === CubiqTS.AllocationType.UNSPAWNEDCOMPONENT;
        riskID = allocation.riskId?.toString();
      }
    }

    if (isSlOrder || isTpOrder) {
      orderInfo = `${clientName} ${clientIntent} ${dealtCCY} ${dealtAmt} ${ccyPair} ${requestRt} ${orderTypeCol} ${strategyDesc}`;
    } else if (isCallOrder) {
      orderInfo = `${clientName} ${ccyPair} ${requestRt} ${orderTypeCol} ${strategyDesc}`;
    } else if (isBmOrder && isComponentLeg) {
      orderInfo = `${clientName} ${clientIntent} ${dealtCCY} ${dealtAmt} ${ccyPair} ${orderTypeCol}   Risk ID: ${riskID}`;
    } else if (isAlgoOrder || isBmOrder || isMarketOrder) {
      orderInfo = `${clientName} ${clientIntent} ${dealtCCY} ${dealtAmt} ${ccyPair} ${orderTypeCol} ${strategyDesc}`;
    }
    return orderInfo;
  }

  /**
   * Creates trader group name(s)from a raw adapter order data
   * @param order the raw order data record
   * @param multiGroup When true, it will check Benchmark orders for presence of multipe groups for crosses
   * @returns
   */
  public createTraderGroupDescription(order: CubiqTS.OrderDataImpl, multiGroup: boolean): string {
    if (order.orderType == CubiqTS.OrderType.BENCHMARK) {
      if (multiGroup) {
        return this._traderGroupNameHelper.getTraderGroupNames({
          data: order,
        } as any);
      } else {
        let traderGroup: CubiqTS.TradingGroupImpl = this.traderGroupLookupService.getTraderGroup(
          order.allocationData[0].deskId,
        );
        if (traderGroup != null) {
          return traderGroup.groupCode;
        }
      }
    }
    if (order.deskId != null) {
      let traderGroup: CubiqTS.TradingGroupImpl = this.traderGroupLookupService.getTraderGroup(
        order.deskId,
      );
      if (traderGroup != null) {
        return traderGroup.groupCode;
      }
    }
    return "-";
  }

  private isSL(orderType: CubiqTS.RequestOrderType): boolean {
    return (
      orderType === CubiqTS.RequestOrderType.SL_BID ||
      orderType === CubiqTS.RequestOrderType.SL_ASK ||
      orderType === CubiqTS.RequestOrderType.SL_ATRATE ||
      orderType === CubiqTS.RequestOrderType.TSL ||
      orderType === CubiqTS.RequestOrderType.TSL_ASK ||
      orderType === CubiqTS.RequestOrderType.TSL_BID
    );
  }

  private isTP(orderType: CubiqTS.RequestOrderType): boolean {
    return orderType === CubiqTS.RequestOrderType.TP;
  }

  private isBmark(orderType: CubiqTS.RequestOrderType): boolean {
    return orderType === CubiqTS.RequestOrderType.BMARK;
  }

  private isMarket(orderType: CubiqTS.RequestOrderType): boolean {
    return orderType === CubiqTS.RequestOrderType.MARKET;
  }

  private isStrategy(order: any): boolean {
    return (
      order &&
      (order.strategyType === CubiqTS.StrategyType.GAMMA ||
        order.siblingRelationship != null ||
        order.parentRelationship != null ||
        (Array.isArray(order.childRelationships) && order.childRelationships.length > 0))
    ); // It might have siblings off on another If-Done axis
  }
}
